import { BasePropertyDTO, EnumElementDTO, ListElementDTO } from 'business/models/Models';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';
import { systemPropertiesCache } from 'generic/systemPropertiesCache';
import { useCallback, useEffect, useRef, useState } from 'react';

type PropertyValueType<T> = T extends { value?: infer V } ? V : never;

export type UseSystemPropertyOptions = {
  /** Refetch when another tab saves this property. */
  refreshOnUpdate?: boolean;
  /** Bypass the in-memory cache and load the latest value on mount. */
  forceRefreshOnMount?: boolean;
};

const resolveOptions = (
  options?: boolean | UseSystemPropertyOptions,
): Required<UseSystemPropertyOptions> => {
  if (typeof options === 'boolean') {
    return { refreshOnUpdate: options, forceRefreshOnMount: false };
  }
  return {
    refreshOnUpdate: Boolean(options?.refreshOnUpdate),
    forceRefreshOnMount: Boolean(options?.forceRefreshOnMount),
  };
};

export function useSystemProperty<T extends BasePropertyDTO & { value?: any }>(
  name: string,
  defaultValue: PropertyValueType<T>,
  options?: boolean | UseSystemPropertyOptions,
): PropertyValueType<T> {
  const { refreshOnUpdate, forceRefreshOnMount } = resolveOptions(options);
  const [value, setValue] = useState<PropertyValueType<T>>(defaultValue);
  const preferencesBroadcastChannel = useBroadcast(CHANNEL_TYPES.PREFERENCES);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchValue = useCallback(
    (forceRefresh = false) => {
      if (forceRefresh) {
        systemPropertiesCache.clear(name);
      }
      systemPropertiesCache
        .get(name)
        .then((v) => {
          if (!mountedRef.current || !v) {
            return;
          }
          const value = (v as T).value;
          if (value instanceof EnumElementDTO) {
            setValue((value as EnumElementDTO).displayName as PropertyValueType<T>);
          } else if (value instanceof ListElementDTO) {
            setValue((value as ListElementDTO).name as PropertyValueType<T>);
          } else {
            setValue(value as PropertyValueType<T>);
          }
        })
        .catch((error) => {
          console.warn(`Failed to load system property ${name}.`, error);
        });
    },
    [name],
  );

  useEffect(() => {
    fetchValue(forceRefreshOnMount);
  }, [fetchValue, forceRefreshOnMount]);

  useEffect(() => {
    if (!refreshOnUpdate || !preferencesBroadcastChannel) {
      return;
    }
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.action === CHANNEL_ACTIONS.PROPERTY_UPDATE &&
        event.data?.payload?.propertyNames?.includes(name)
      ) {
        fetchValue(true);
      }
    };
    preferencesBroadcastChannel.addEventListener('message', handleMessage);
    return () =>
      preferencesBroadcastChannel.removeEventListener('message', handleMessage);
  }, [refreshOnUpdate, preferencesBroadcastChannel, fetchValue, name]);

  return value;
}
